import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

/* =========================
   Helpers
   ========================= */

// Ordenação por ordem -> nome
const sortFn = (a, b) => {
  const ao = Number(a.ordem || 0);
  const bo = Number(b.ordem || 0);
  if (ao !== bo) return ao - bo;
  const an = String(a.nome || "");
  const bn = String(b.nome || "");
  return an.localeCompare(bn, "pt-BR", { sensitivity: "base" });
};

// Monta hierarquia a partir da lista plana (usa id_pai)
function buildCategoryTree(list = []) {
  const nodes = list.map((it) => ({
    ...it,
    children: Array.isArray(it.children) ? it.children : [],
  }));

  const byId = new Map(nodes.map((n) => [Number(n.id), n]));
  const roots = [];

  for (const n of nodes) {
    const pid = Number(n.id_pai || 0);
    if (pid > 0 && byId.has(pid)) {
      byId.get(pid).children.push(n);
    } else {
      roots.push(n);
    }
  }

  roots.sort(sortFn);
  for (const n of nodes) {
    if (n.children?.length) n.children.sort(sortFn);
  }

  return roots;
}

/** Achata a árvore para listas planas (útil em selects que não são hierárquicos) */
function flattenTree(tree = []) {
  const out = [];
  const walk = (n) => {
    out.push(n);
    n.children?.forEach(walk);
  };
  tree.forEach(walk);
  return out;
}

/* =========================
   Hook principal
   =========================
   Endpoints:
   - GET    /categories?page=&limit=&search=
   - GET    /categories?parentId=:id_pai
   - POST   /categories
   - PUT    /categories/:id
   - DELETE /categories/:id
*/
export const useCategories = ({ page = 1, limit = 100, search = "" } = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["categories", { page, limit, search }],
    async () =>
      fetchData(
        `/categories?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      ),
    {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      select: (response) => {
        const payload = response?.data || {};
        const flat = Array.isArray(payload) ? payload : [];
        const tree = buildCategoryTree(flat);
        const pagination = payload?.pagination || {
          total: flat.length,
          page,
          limit,
          totalPages: Math.max(1, Math.ceil((flat.length || 0) / (limit || 1))),
        };
        return { tree, flat, pagination };
      },
    }
  );

  /** Buscar subcategorias pelo id do pai (para lazy-load quando precisar) */
  const useFetchSubcategories = (parentId) =>
    useQuery(
      ["subcategories", Number(parentId || 0)],
      async () => {
        const res = await fetchData(`/categories?parentId=${parentId}`);
        return Array.isArray(res?.data) ? res.data.sort(sortFn) : [];
      },
      { enabled: !!parentId, staleTime: 10_000, refetchOnWindowFocus: false }
    );

  /* ========== Mutations CRUD ========== */
  const createMutation = useMutation(
    (category) => fetchData(`/categories`, "POST", category),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("categories");
        queryClient.invalidateQueries("subcategories");
      },
    }
  );

  const updateMutation = useMutation(
    (category) => {
      if (!category?.id) throw new Error("ID obrigatório para atualizar categoria.");
      return fetchData(`/categories/${category.id}`, "PUT", category);
    },
    {
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries("categories");
        if (vars?.id_pai) queryClient.invalidateQueries(["subcategories", Number(vars.id_pai)]);
      },
    }
  );

  const deleteMutation = useMutation(
    (payload) => {
      const id = payload && typeof payload === "object" ? payload.id : payload;
      const resolutions = payload && typeof payload === "object" ? payload.resolutions : undefined;
      return fetchData(`/categories/${id}`, "DELETE", resolutions ? { resolutions } : {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("categories");
        queryClient.invalidateQueries("subcategories");
      },
    }
  );

  /** Atalhos específicos para subcategorias */
  const createSubcategory = (parentId, sub) =>
    createMutation.mutateAsync({
      nome: sub.nome,
      status: sub.status ?? 1,
      id_pai: Number(parentId),
      ...(sub.ordem != null ? { ordem: sub.ordem } : {}),
      ...(sub.imagem ? { imagem: sub.imagem } : {}),
    });

  const updateSubcategory = (subId, sub) =>
    updateMutation.mutateAsync({
      id: subId,
      nome: sub.nome,
      status: sub.status ?? 1,
      ...(sub.ordem != null ? { ordem: sub.ordem } : {}),
      ...(sub.imagem ? { imagem: sub.imagem } : {}),
    });

  const deleteSubcategory = (subId, resolutions) =>
    deleteMutation.mutateAsync(resolutions ? { id: subId, resolutions } : subId);

  /* ========== Derivados úteis (memoizados) ========== */
  const data = query.data || { tree: [], flat: [], pagination: { page, limit, total: 0 } };

  // Opções para Autocomplete de categoria (nível 1) e subcategoria (filhas)
  const rootCategories = useMemo(
    () => (data.tree || []).map((c) => ({ id: c.id, nome: c.nome, children: c.children || [] })),
    [data.tree]
  );

  const allAsOptions = useMemo(
    () => flattenTree(data.tree || []).map((c) => ({ id: c.id, label: c.nome, id_pai: c.id_pai })),
    [data.tree]
  );

  return {
    ...query,              // { data: {tree, flat, pagination}, isLoading, ... }
    data,                  // alias
    rootCategories,        // só as raízes (para o seu Autocomplete de categoria)
    allAsOptions,          // tudo “achado” em { id, label, id_pai }
    useFetchSubcategories, // hook auxiliar
    // mutations
    createMutation,
    updateMutation,
    deleteMutation,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
  };
};
